function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
window.recentlyViewedIds = [];

const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),

  open(panel) {
    if (!panel || this.stack.includes(panel)) return;
    this.stack.push(panel);
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    panel.querySelector('.side-panel-close')?.focus();
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) {
      handler(() => this._close(panel));
    } else {
      this._close(panel);
    }
  },

  _close(panel) {
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    panel.classList.remove('active');
    panel.inert = true;
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
    }
    dispatchCustomEvent('panel:closed', { panel });
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  }
};

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });

      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));
    }

    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }

    change_quantity(change) {
      let quantity = Number(this.input.value);

      if (isNaN(quantity)) quantity = 1;

      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }

      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }

      quantity += change;
      quantity = Math.max(quantity, 1);

      this.input.value = quantity;
      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);

      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }

      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }

  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
    }

    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card--featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');

      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }

      if (this.additional_images) {
        this.enableAdditionalImages();
      }

      if (this.quick_add) {
        this.quick_add.addEventListener('click', this.quickAdd.bind(this));
      }

      if (this.size_options) {
        this.enableSizeOptions();
        if (this.size_options.dataset.index === "1" && this.size_options.querySelectorAll(".product-card-sizes--size").length > 1) {
          this.ensureSizeChoiceLinks();
          this._sizeLinkObserver = new MutationObserver(() => this.ensureSizeChoiceLinks());
          this._sizeLinkObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ["href", "data-href"] });
        }
      }

      this.observeVideo();
    }

    disconnectedCallback() {
      if (this._sizeLinkObserver) {
        this._sizeLinkObserver.disconnect();
        this._sizeLinkObserver = null;
      }
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    ensureSizeChoiceLinks() {
      for (const link of this.querySelectorAll(".product-card--featured-image-link, .product-card-title, .product-card-swatch")) {
        const attribute = link.matches("a") ? "href" : "data-href";
        const original = link.getAttribute(attribute);
        if (!original || !original.includes("/products/") || !original.includes("variant=") || original.includes("choose_size=")) continue;
        const url = new URL(original, window.location.origin);
        url.searchParams.set("choose_size", "1");
        link.setAttribute(attribute, url.pathname + url.search + url.hash);
      }
    }
    observeVideo() {
      var template = this.querySelector('.product-card__video-deferred');
      if (!template) return;

      var self = this;

      this._observer = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          self._observer.disconnect();
          self._observer = null;
          template.replaceWith(template.content.cloneNode(true));
        }
      }, { rootMargin: '200px' });

      this._observer.observe(this);
    }

    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let hasNav = nav.length > 0;
      let image_container = this.querySelector('.product-card--featured-image-link');
      let lastIndex = -1;

      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let sel = Math.floor((l / w) * image_length);

        if (sel === lastIndex || !images[sel]) {
          return;
        }

        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');

          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }

        images[sel].classList.add('hover');

        if (hasNav) {
          nav[sel].classList.add('active');
        }

        lastIndex = sel;
      };

      const mouseleave = function () {
        if (lastIndex >= 0 && images[lastIndex]) {
          images[lastIndex].classList.remove('hover');

          if (hasNav) {
            nav[lastIndex].classList.remove('active');
          }
        }

        lastIndex = -1;
      };

      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });

        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });

        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });

        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });

        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });

        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      window.addEventListener('load', () => {
        images.forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });
    }    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? (image.dataset.srcset || image.getAttribute('srcset') || '') : '',
        org_src = image ? (image.getAttribute('src') || '') : '';

      this.color_index = this.swatches.dataset.index;
      this._activeSwatch = null;
      let lastX = null, lastY = null;

      // ▼ AJOUT : coloris par défaut en 1re position → toujours visible dans les 3
      const defaultSwatch = swatches.querySelector('.product-card-swatch.active');
      if (defaultSwatch) swatches.prepend(defaultSwatch);

      window.addEventListener('load', () => {
        swatch_list.forEach((swatch) => {
          let img = new Image();
          img.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(img);
        });
      });

      const applySwatch = (swatch) => {
        if (this._activeSwatch === swatch) return;

        this._activeSwatch = swatch;

        swatch_list.forEach((el) => el.classList.remove('active'));

        if (image && swatch.dataset.srcset) {
          image.setAttribute('srcset', swatch.dataset.srcset);
        }

        if (this.size_options) {
          this.current_options[this.color_index] = swatch.querySelector('span').innerText;
          this.updateMasterId();
        }

        swatch.classList.add('active');
      };

      const resetSwatch = () => {
        if (this._activeSwatch === null) return;

        this._activeSwatch = null;
        lastX = lastY = null;

        swatch_list.forEach((el) => el.classList.remove('active'));

        if (defaultSwatch) defaultSwatch.classList.add('active');

        if (image) {
          if (org_srcset) image.setAttribute('srcset', org_srcset);
          if (org_src) image.setAttribute('src', org_src);
        }
      };

      swatch_list.forEach((swatch) => {
        swatch.addEventListener('mouseover', (e) => {
          if (e.clientX === lastX && e.clientY === lastY) return;

          lastX = e.clientX;
          lastY = e.clientY;

          applySwatch(swatch);
        });

        swatch.addEventListener('click', function (evt) {
          evt.preventDefault();
          window.location.href = this.dataset.href;
        });
      });

      swatches.addEventListener('mouseleave', resetSwatch);
    }

    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-card--featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });

      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });

      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();

          if (size.classList.contains('is-disabled')) {
            return;
          }

          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();
          this.addToCart(this.currentVariant.id, size);
        });
      });
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });

      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(
        this.querySelector('[type="application/json"]').textContent
      );

      return this.variantData;
    }

    setDisabled() {
      const variant_data = this.getVariantData();

      if (!variant_data) {
        return true;
      }

      const size_buttons = this.size_options.querySelectorAll('.product-card-sizes--size');

      if (this.currentVariant) {
        const selected_options = this.currentVariant.options.map((value, index) => ({
          value,
          index: `option${index + 1}`
        }));

        const available_options = this.createAvailableOptionsTree(
          variant_data,
          selected_options
        );

        const fieldset_options = Object.values(available_options)[this.size_index];

        if (fieldset_options) {
          size_buttons.forEach((input, input_i) => {
            input.classList.toggle(
              'is-disabled',
              fieldset_options[input_i].isUnavailable
            );
          });
        }
      } else {
        size_buttons.forEach((input) => {
          input.classList.add('is-disabled');
        });
      }

      return true;
    }

    createAvailableOptionsTree(variant_data, selected_options) {
      return variant_data.reduce((options, variant) => {

        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(
            option => option.value === variant[index]
          );

          if (typeof entry === 'undefined') {
            entry = {
              value: variant[index],
              isUnavailable: true
            };

            options[index].push(entry);
          }

          const countVariantOptionsThatMatchCurrent = selected_options.reduce(
            (count, { value, index }) => {
              return variant[index] === value ? count + 1 : count;
            },
            0
          );

          if (
            countVariantOptionsThatMatchCurrent >=
            selected_options.length - 1
          ) {
            entry.isUnavailable =
              entry.isUnavailable && variant.available
                ? false
                : entry.isUnavailable;
          }

          if (
            (!this.currentVariant || !this.currentVariant.available) &&
            selected_options.find(
              (option) =>
                option.value === entry.value &&
                index === option.index
            )
          ) {
            entry.isUnavailable = true;
          }

          if (index === 'option1') {
            entry.isUnavailable =
              entry.isUnavailable && variant.available
                ? false
                : entry.isUnavailable;
          }
        });

        return options;

      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }

    addToCart(variantId, button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', true);

      const formData = new FormData();

      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append(
        'sections',
        this.getSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        },
        body: formData
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items')
              ? response.items[0]
              : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }

    quickAdd(evt) {
      evt.preventDefault();

      if (this.quick_add.disabled) {
        return;
      }

      this.addToCart(
        this.quick_add.dataset.productId,
        this.quick_add
      );
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {

        if (!document.getElementById(section.id)) {
          return;
        }

        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);

        elementToReplace.innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
      }));

      const cartDrawer = document.getElementById('Cart-Drawer');

      if (cartDrawer) {
        OverlayManager.open(cartDrawer);

        dispatchCustomEvent('cart-drawer:open');
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }

  customElements.define('product-card', ProductCard);
}
/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');

      this.onClick = () => {
        OverlayManager.closeLast();
      };

      this.addEventListener('click', this.onClick);
      document.addEventListener('panel:close', this.onClick);

      if (!this.cc.hasAttribute('initialized')) {
        this.cc.addEventListener('click', this.onClick);
        this.cc.setAttribute('initialized', '');
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  // Product drawer: close images container first, then drawer
  const productDrawer = document.getElementById('Product-Drawer');
  if (productDrawer) {
    OverlayManager.registerBeforeClose(productDrawer, (done) => {
      const container = productDrawer.querySelector('.product-quick-images--container');
      if (container && container.classList.contains('active') && window.innerWidth >= 1069) {
        container.classList.remove('active');
        container.addEventListener('transitionend', () => {
          productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
          done();
        }, { once: true });
      } else {
        container?.classList.remove('active');
        productDrawer.querySelector('#Product-Drawer-Content').innerHTML = '';
        done();
      }
    });

    document.addEventListener('panel:open', (e) => {
      if (e.detail.panel === productDrawer) {
        setTimeout(() => {
          productDrawer.querySelector('.product-quick-images--container')?.classList.add('active');
        });
      }
    });
  }

  document.addEventListener('keyup', (e) => {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      OverlayManager.closeLast();
    }
  });
}

/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');
      if (!button) return;

      button.addEventListener('click', (e) => {
        e.preventDefault();
        OverlayManager.open(this);
        dispatchCustomEvent('cart-drawer:open');
      });

      document.addEventListener('panel:open', (e) => {
        if (e.detail.panel === this) {
          setTimeout(() => {
            this.querySelector('.product-recommendations--full')?.classList.add('active');
          });
        }
      });

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', (event) => {
        this.refresh();
      });

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      OverlayManager.registerBeforeClose(this, (done) => {
        const recs = this.querySelector('.product-recommendations--full');
        if (recs && recs.classList.contains('active') && window.innerWidth >= 1069) {
          recs.classList.remove('active');
          recs.addEventListener('transitionend', () => done(), { once: true });
        } else {
          recs?.classList.remove('active');
          done();
        }
      });
    }

    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(
          event.target.dataset.index,
          event.target.value
        );
      }
    }

    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.side-panel-inner'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });

      this.querySelector('.product-recommendations--full')?.classList.remove('active');

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);

            if (parsedState.sections) {
              elementToReplace.innerHTML =
                this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
            }
          }));

          dispatchCustomEvent('line-item:change:end', {
            quantity: quantity,
            cart: parsedState
          });

          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        });
    }

    refresh() {
      this.querySelector('.product-recommendations--full')?.classList.remove('active');

      let sections = 'cart-drawer,cart-bubble';

      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);

            elementToReplace.innerHTML =
              this.getSectionInnerHTML(
                parsedState[section.section],
                section.selector
              );
          }));
        });
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListeners();
    }

    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));

      const details = this.closest('details');

      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }

      this.resizeSelect();
    }

    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText =
        'visibility:hidden;position:fixed;pointer-events:none';

      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width =
            `${tempSelect.clientWidth + 10}px`;
        }

        tempSelect.remove();
      });
    }
  }

  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const quickViewCache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(e) {
      e.preventDefault();

      let productHandle = this.dataset.productHandle;

      if (!productHandle) {
        return;
      }

      let rootUrl = theme.routes.root_url.replace(/\/+$/, '');
      let href = `${rootUrl}/products/${productHandle}?view=quick-view`;

      if (this.classList.contains('loading')) {
        return;
      }

      this.classList.add('loading');

      // Abort any in-flight quick-view fetch
      if (QuickView.activeController) {
        QuickView.activeController.abort();
      }

      let controller = new AbortController();
      QuickView.activeController = controller;

      // Serve from cache if available
      if (quickViewCache.has(productHandle)) {
        this.classList.remove('loading');
        this.renderQuickView(
          quickViewCache.get(productHandle),
          href,
          productHandle
        );
        return;
      }

      fetch(href, {
        method: 'GET',
        signal: controller.signal
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Quick view fetch failed: ${response.status}`
            );
          }

          return response.text();
        })
        .then(text => {
          let parsed = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content');

          if (!parsed) {
            throw new Error(
              'Quick view content not found in response'
            );
          }

          let sectionInnerHTML = parsed.innerHTML;

          quickViewCache.set(
            productHandle,
            sectionInnerHTML
          );

          this.renderQuickView(
            sectionInnerHTML,
            href,
            productHandle
          );
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        })
        .finally(() => {
          this.classList.remove('loading');
        });
    }

    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;

      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(
            attr.name,
            attr.value
          );
        });

        if (oldScript.src) {
          if (
            document.querySelector(
              `script[src="${oldScript.src}"]`
            )
          ) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }

        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(
            newScript,
            oldScript
          );
        } else {
          head.appendChild(newScript);
        }
      });
    }

    renderQuickView(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }

      let drawerContent =
        this.drawer.querySelector('#Product-Drawer-Content');

      drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(drawerContent);

      requestAnimationFrame(() => {
        if (
          typeof Shopify !== 'undefined' &&
          Shopify.PaymentButton
        ) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      });

      OverlayManager.open(this.drawer);

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });

      addIdToRecentlyViewed(productHandle);
    }
  }

  QuickView.activeController = null;
  customElements.define('quick-view', QuickView);
}
/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.buttons = this.querySelectorAll('.side-panel-content-tabs--button');
      this.contents = this.querySelectorAll('.side-panel-content-tabs--content');

      this.buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();

          const target = button.dataset.target;

          this.buttons.forEach((item) => {
            item.classList.remove('active');
          });

          this.contents.forEach((content) => {
            content.classList.remove('active');
          });

          button.classList.add('active');

          const targetContent = this.querySelector(`#${target}`);

          if (targetContent) {
            targetContent.classList.add('active');
          }
        });
      });
    }
  }

  customElements.define(
    'side-panel-content-tabs',
    SidePanelContentTabs
  );
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.details = this.querySelector('details');

      if (!this.details) {
        return;
      }

      this.summary = this.details.querySelector('summary');
      this.content = this.details.querySelector('.collapsible__content');

      if (!this.summary || !this.content) {
        return;
      }

      this.summary.addEventListener(
        'click',
        this.onSummaryClick.bind(this)
      );
    }

    onSummaryClick(event) {
      event.preventDefault();

      if (this.details.open) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.details.open = true;

      const animation = this.content.animate(
        [
          {
            height: 0,
            opacity: 0
          },
          {
            height: `${this.content.scrollHeight}px`,
            opacity: 1
          }
        ],
        {
          duration: 250,
          easing: 'ease'
        }
      );

      animation.onfinish = () => {
        this.content.style.height = 'auto';
      };
    }

    close() {
      const animation = this.content.animate(
        [
          {
            height: `${this.content.scrollHeight}px`,
            opacity: 1
          },
          {
            height: 0,
            opacity: 0
          }
        ],
        {
          duration: 250,
          easing: 'ease'
        }
      );

      animation.onfinish = () => {
        this.details.open = false;
        this.content.style.height = '';
      };
    }
  }

  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 * Recently viewed products
 */
function addIdToRecentlyViewed(productId) {
  if (!productId) {
    return;
  }

  let recentlyViewed = [];

  try {
    recentlyViewed = JSON.parse(
      localStorage.getItem('recently-viewed-products') || '[]'
    );
  } catch (e) {
    recentlyViewed = [];
  }

  recentlyViewed = recentlyViewed.filter(
    (item) => item !== productId
  );

  recentlyViewed.unshift(productId);

  recentlyViewed = recentlyViewed.slice(0, 10);

  localStorage.setItem(
    'recently-viewed-products',
    JSON.stringify(recentlyViewed)
  );

  window.recentlyViewedIds = recentlyViewed;
}

try {
  window.recentlyViewedIds = JSON.parse(
    localStorage.getItem('recently-viewed-products') || '[]'
  );
} catch (e) {
  window.recentlyViewedIds = [];
}